import { useState, useEffect } from 'react';
import { PropertySearch } from '@/components/property/PropertySearch';
import { PropertyGrid } from '@/components/property/PropertyGrid';
import { PageLayout } from '@/components/layout/PageLayout';
import { ResultsHeader } from '@/components/common/ResultsHeader';
import { useProperties } from '@/hooks/useProperties';
import { usePage } from '@/hooks/usePage';
import { PropertyFilters } from '@/types/property';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEO } from '@/components/shared/SEO';
import { deserializeFilters, serializeFilters, validateFilters } from '@/utils/urlFilters';
import { showErrorToast } from '@/utils/toast';
import { PageSkeleton } from '@/components/ui/Skeleton';

export default function ArriendosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Cargar configuración de la página desde el backend
  const { page, viewData, loading: loadingPage, error: pageError } = usePage('arriendos');

  // Initialize filters from URL params using urlFilters utility
  const [filters, setFilters] = useState<PropertyFilters>(() => {
    // Deserialize filters from URL
    const urlFilters = deserializeFilters(searchParams);

    // Validate and clean filters
    const validatedFilters = validateFilters(urlFilters);

    // Set default operation
    validatedFilters.operation = 'ARRIENDO';

    return validatedFilters;
  });

  const { properties, loading: loadingProperties, error: propertiesError } = useProperties({
    filters,
    sortBy: 'publishedAt',
    sortOrder: 'desc'
  });

  // Show toast notification when error occurs
  useEffect(() => {
    if (propertiesError) {
      showErrorToast(propertiesError);
    }
  }, [propertiesError]);

  // Sync filters with URL when browser navigation (back/forward) happens
  useEffect(() => {
    const urlFilters = deserializeFilters(searchParams);
    const validatedFilters = validateFilters(urlFilters);

    // Set default operation
    validatedFilters.operation = 'ARRIENDO';

    // Only update if filters actually changed to avoid infinite loops
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(validatedFilters);
    if (filtersChanged) {
      setFilters(validatedFilters);
    }
  }, [searchParams]); // Re-run when URL changes (back/forward buttons)

  const handleSearch = (newFilters: PropertyFilters) => {
    // Maintain default filters
    const defaultFilters = { operation: 'ARRIENDO' };
    const updatedFilters = { ...defaultFilters, ...newFilters };

    // Validate filters before applying
    const validatedFilters = validateFilters(updatedFilters);
    setFilters(validatedFilters);

    // Serialize filters to URL params using utility
    const params = serializeFilters(validatedFilters);

    // Remove 'operation' from URL if it's the default
    if (validatedFilters.operation === 'ARRIENDO') {
      params.delete('operation');
    }

    setSearchParams(params);
  };

  const handleHeroSearch = (query: string) => {
    navigate(`/arriendos?location=${encodeURIComponent(query)}`);
  };

  // Show loading while page config is loading
  if (loadingPage) {
    return <PageSkeleton message="Cargando página..." />;
  }

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/arriendos`;
  const propertyCount = properties.length;

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": page?.seoTitle || "Propiedades en Arriendo",
        "description": page?.seoDescription || `Encuentra ${propertyCount} propiedades en arriendo. Casas, departamentos y oficinas con excelentes condiciones.`,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Arriendos",
              "item": currentUrl
            }
          ]
        }
      }
    ]
  };

  // Use dynamic data from backend if available, fallback to defaults
  const heroTitle = viewData?.hero?.title || "Propiedades en Arriendo";
  const heroSubtitle = viewData?.hero?.subtitle || "Encuentra el lugar perfecto para vivir con las mejores condiciones de arriendo";
  const heroImage = viewData?.hero?.backgroundImage || "/images/hero/arriendos-hero.jpg";
  const heroShowSearch = viewData?.hero?.showSearch ?? true;

  // SEO data
  const seoTitle = page?.seoTitle || `Propiedades en Arriendo - ${propertyCount} Opciones`;
  const seoDescription = page?.seoDescription || "Busca propiedades en arriendo: casas, apartamentos y locales comerciales. Alquila tu espacio ideal con condiciones flexibles.";
  const seoKeywords = page?.seoKeywords || ["arriendo propiedades", "alquiler casas", "departamentos arriendo", "propiedades para arrendar", "bienes raíces arriendo", "alquiler inmuebles"];

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath="/arriendos"
        ogImage={heroImage}
      />

      <Helmet>
        {/* Additional OG Tags */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Propiedades en arriendo disponibles" />
        <meta property="og:locale" content="es_ES" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PageLayout
        hero={{
          title: heroTitle,
          subtitle: heroSubtitle,
          backgroundImage: heroImage,
          showSearch: heroShowSearch,
          onSearch: handleHeroSearch
        }}
        error={propertiesError}
        emptyState={!loadingProperties && properties.length === 0 ? {
          title: "No se encontraron propiedades con los filtros seleccionados",
          description: "Intenta ajustar los filtros para encontrar más opciones"
        } : undefined}
      >
        {/* Property Search Filters */}
        <div className="mb-8">
          <PropertySearch
            onSearch={handleSearch}
            initialFilters={filters}
            filtersConfig={viewData?.filters}
            loading={loadingPage}
          />
        </div>

        <ResultsHeader
          title="Propiedades en Arriendo"
          count={properties.length}
          loading={loadingProperties}
          itemName="propiedad"
        />

        <PropertyGrid
          properties={properties}
          loading={loadingProperties}
        />
      </PageLayout>
    </>
  );
}
